import { useMemo, useState, useEffect, useRef } from "react";
import { sharedObtenerIcon } from "../utils/shared-functions";

/** Max waypoints per Directions request (Google limit 25). Each batch = origin + 25 waypoints + destination = 26 legs. */
const POINTS_PER_BATCH = 27;
const DELAY_BETWEEN_REQUESTS_MS = 2000;

/** Size in pixels for markers that use an icon URL (e.g. punto actual from sharedObtenerIcon). */
const MARKER_ICON_URL_SIZE = 42;

/** Decode Google encoded polyline to array of { lat, lng }. */
function decodePolyline(encoded) {
    const points = [];
    let index = 0;
    let lat = 0;
    let lng = 0;
    while (index < encoded.length) {
        let b; let shift = 0; let result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
        lat += dlat;
        shift = 0; result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
        lng += dlng;
        points.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }
    return points;
}

function getRandomColor() {
    return "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0");
}

/**
 * Map component for the ruteo (routing) page.
 * Uses its own map instance (no MapaGoogle) so we can draw route polylines here
 * without changing mapa-google.js. Renders markers (items + puntoActual) and
 * optional route legs (origin = puntoActual, then items in order; each segment random color).
 */
export default function MapaRuteo({
    items = [],
    puntoActual = null,
    drawRoute = false,
    center = null,
    zoom = 12,
    onMarkerClick,
    onMapClick,
    onRouteLegsChange,
}) {
    const [routeLegs, setRouteLegs] = useState([]);
    const routeFetchCancelledRef = useRef(false);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const routePolylinesRef = useRef([]);
    const initialFitBoundsDoneRef = useRef(false);

    const markers = useMemo(() => {
        const itemMarkers = items
            .filter((item) => item.latitud && item.longitud)
            .map((item, index) => ({
                lat: parseFloat(item.latitud),
                lng: parseFloat(item.longitud),
                title: item.cliente || "Item",
                infoWindow: item.cliente || "Item",
                color: item.color || "#000000",
                index: index + 1,
            }));

        const lat = puntoActual != null
            ? parseFloat(puntoActual.LATITUD ?? puntoActual.latitud)
            : NaN;
        const lng = puntoActual != null
            ? parseFloat(puntoActual.LONGITUD ?? puntoActual.longitud)
            : NaN;
        const hasValidPuntoActual = !Number.isNaN(lat) && !Number.isNaN(lng) && isFinite(lat) && isFinite(lng);

        const puntoActualMarker = hasValidPuntoActual
            ? [{
                lat,
                lng,
                title: puntoActual.ID_ACTIVO ?? puntoActual.idActivo ?? "Punto actual",
                infoWindow: "Punto actual del vehículo",
                color: "#2563eb",
                index: "",
                iconUrl: sharedObtenerIcon(
                    puntoActual.ORIENT ?? 0,
                    false,
                    puntoActual.I_ENCENDIDO ?? 1,
                    puntoActual.VELOCIDAD ?? 0,
                    puntoActual.icono ?? 0
                ),
            }]
            : [];

        return [...itemMarkers, ...puntoActualMarker];
    }, [items, puntoActual]);

    // Fetch directions when drawRoute is true
    useEffect(() => {
        if (!drawRoute) {
            setRouteLegs([]);
            if (onRouteLegsChange) onRouteLegsChange([]);
            return;
        }

        const originLat = puntoActual != null
            ? parseFloat(puntoActual.LATITUD ?? puntoActual.latitud)
            : NaN;
        const originLng = puntoActual != null
            ? parseFloat(puntoActual.LONGITUD ?? puntoActual.longitud)
            : NaN;
        const hasOrigin = !Number.isNaN(originLat) && !Number.isNaN(originLng) && isFinite(originLat) && isFinite(originLng);
        const waypointsWithCoords = (items || []).filter((item) => item.latitud != null && item.longitud != null);

        if (!hasOrigin || waypointsWithCoords.length === 0) {
            setRouteLegs([]);
            if (onRouteLegsChange) onRouteLegsChange([]);
            return;
        }

        const pointLabels = [
            "Vehículo actual",
            ...waypointsWithCoords.map((item) => item.cliente || item.nombre || "Parada"),
        ];
        const orderedPoints = [
            { lat: originLat, lng: originLng },
            ...waypointsWithCoords.map((item) => ({
                lat: parseFloat(item.latitud),
                lng: parseFloat(item.longitud),
            })),
        ];

        routeFetchCancelledRef.current = false;
        setRouteLegs([]);
        if (onRouteLegsChange) onRouteLegsChange([]);

        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        const requestDirections = (origin, destination, waypoints) => {
            return new Promise((resolve, reject) => {
                if (!window.google || !window.google.maps) {
                    reject(new Error("Google Maps not loaded"));
                    return;
                }
                const service = new window.google.maps.DirectionsService();
                const req = {
                    origin: { lat: origin.lat, lng: origin.lng },
                    destination: { lat: destination.lat, lng: destination.lng },
                    travelMode: window.google.maps.TravelMode.DRIVING,
                };
                if (waypoints && waypoints.length > 0) {
                    req.waypoints = waypoints.map((p) => ({
                        location: new window.google.maps.LatLng(p.lat, p.lng),
                        stopover: true,
                    }));
                }
                service.route(req, (result, status) => {
                    if (status !== window.google.maps.DirectionsStatus.OK) {
                        reject(new Error(status));
                        return;
                    }
                    resolve(result);
                });
            });
        };

        const getPathFromLeg = (leg) => {
            const path = [];
            if (leg.steps) {
                leg.steps.forEach((step) => {
                    const encoded = step.polyline && step.polyline.points;
                    if (encoded && typeof encoded === "string") {
                        path.push(...decodePolyline(encoded));
                    } else if (step.start_location && step.end_location) {
                        path.push(
                            { lat: step.start_location.lat(), lng: step.start_location.lng() },
                            { lat: step.end_location.lat(), lng: step.end_location.lng() }
                        );
                    }
                });
            }
            return path;
        };

        (async () => {
            const n = orderedPoints.length;
            const numBatches = Math.ceil((n - 1) / (POINTS_PER_BATCH - 1));
            const allLegs = [];

            for (let i = 0; i < numBatches; i++) {
                if (routeFetchCancelledRef.current) return;
                if (i > 0) await delay(DELAY_BETWEEN_REQUESTS_MS);
                if (routeFetchCancelledRef.current) return;

                const startIdx = i * (POINTS_PER_BATCH - 1);
                const endIdx = Math.min(startIdx + POINTS_PER_BATCH, n);
                const origin = orderedPoints[startIdx];
                const destination = orderedPoints[endIdx - 1];
                const waypoints = endIdx - startIdx > 2
                    ? orderedPoints.slice(startIdx + 1, endIdx - 1)
                    : [];

                try {
                    const result = await requestDirections(origin, destination, waypoints);
                    const route = result.routes && result.routes[0];
                    if (!route || !route.legs) continue;
                    route.legs.forEach((leg, legIdx) => {
                        const path = getPathFromLeg(leg);
                        if (path.length >= 2) {
                            const fromLabel = pointLabels[startIdx + legIdx] || "Origen";
                            const toLabel = pointLabels[startIdx + legIdx + 1] || "Destino";
                            allLegs.push({
                                path,
                                color: getRandomColor(),
                                duration: leg.duration || { text: "", value: 0 },
                                distance: leg.distance || { text: "", value: 0 },
                                fromLabel,
                                toLabel,
                            });
                        }
                    });
                    setRouteLegs([...allLegs]);
                    if (onRouteLegsChange) onRouteLegsChange([...allLegs]);
                } catch (err) {
                    console.warn("Directions request failed:", err);
                }
            }
        })();

        return () => {
            routeFetchCancelledRef.current = true;
        };
        // Do not depend on puntoActual: it updates every 15s and would re-fetch the route and change colors
    }, [drawRoute, items, onRouteLegsChange]);

    const [map, setMap] = useState(null);
    const [userLocation, setUserLocation] = useState(null);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                () => setUserLocation({ lat: -16.5, lng: -63.6 })
            );
        } else {
            setUserLocation({ lat: -16.5, lng: -63.6 });
        }
    }, []);

    useEffect(() => {
        if (!window.google || !window.google.maps || !mapRef.current || map) return;
        const defaultCenter = { lat: -16.5, lng: -63.6 };
        const mapCenter = center || userLocation || defaultCenter;
        const googleMap = new window.google.maps.Map(mapRef.current, {
            center: mapCenter,
            zoom: zoom,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
        });
        if (onMapClick) {
            googleMap.addListener("click", (event) => {
                onMapClick(event.latLng.lat(), event.latLng.lng());
            });
        }
        mapInstanceRef.current = googleMap;
        setMap(googleMap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation]);

    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return;
        // Explicit center (e.g. from item click): always apply so item click works with or without route
        if (center && center.lat !== undefined && center.lng !== undefined) {
            const lat = parseFloat(center.lat);
            const lng = parseFloat(center.lng);
            if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
                map.panTo(new window.google.maps.LatLng(lat, lng));
                if (zoom != null) map.setZoom(zoom);
            }
            return;
        }
        // When a route is shown and no explicit center, keep the map fitted to the route
        if (routeLegs && routeLegs.length > 0) return;
        if (userLocation) {
            map.setCenter(new window.google.maps.LatLng(userLocation.lat, userLocation.lng));
            if (zoom != null) map.setZoom(zoom);
        }
    }, [map, center, userLocation, zoom, routeLegs]);

    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return;
        // Don't override zoom when route is shown unless we have an explicit center (handled above)
        if (routeLegs && routeLegs.length > 0 && !center) return;
        map.setZoom(zoom);
    }, [map, zoom, routeLegs, center]);

    // Markers: same logic as MapaGoogle (simplified: add markers to map, no clusterer to reduce code)
    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return;
        const existingMarkers = map._ruteoMarkers || [];
        existingMarkers.forEach((m) => m.setMap(null));
        map._ruteoMarkers = [];

        markers.forEach((markerData) => {
            const color = markerData.color || "#000000";
            const index = markerData.index !== undefined ? markerData.index : 0;
            let icon;
            if (markerData.iconUrl) {
                icon = {
                    url: markerData.iconUrl,
                    scaledSize: new window.google.maps.Size(MARKER_ICON_URL_SIZE, MARKER_ICON_URL_SIZE),
                    anchor: new window.google.maps.Point(MARKER_ICON_URL_SIZE / 2, MARKER_ICON_URL_SIZE / 2),
                };
            } else {
                const pinPath = "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";
                icon = {
                    path: pinPath,
                    fillColor: color,
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 2,
                    scale: 2.0,
                    anchor: new window.google.maps.Point(12, 22),
                    labelOrigin: new window.google.maps.Point(12, 9.5),
                };
            }
            let label = null;
            if (index !== "" && index != null && index !== undefined && !markerData.iconUrl) {
                label = { text: String(index), color: "#0d0c0c", fontSize: "12px", fontWeight: "bold" };
            }
            const marker = new window.google.maps.Marker({
                position: { lat: markerData.lat, lng: markerData.lng },
                title: markerData.title || "",
                icon,
                label,
                map,
            });
            if (markerData.infoWindow) {
                const iw = new window.google.maps.InfoWindow({ content: markerData.infoWindow });
                marker.addListener("click", () => {
                    iw.open(map, marker);
                    if (onMarkerClick) onMarkerClick(markerData);
                });
            } else if (onMarkerClick) {
                marker.addListener("click", () => onMarkerClick(markerData));
            }
            map._ruteoMarkers.push(marker);
        });

        // Fit bounds only once on first render when we have markers (do not refit when puntoActual updates every 15s)
        if (!center && map._ruteoMarkers.length > 0 && !initialFitBoundsDoneRef.current) {
            const bounds = new window.google.maps.LatLngBounds();
            map._ruteoMarkers.forEach((m) => bounds.extend(m.getPosition()));
            const padding = 60;
            map.fitBounds(bounds, padding);
            initialFitBoundsDoneRef.current = true;
        }
    }, [map, markers, onMarkerClick, center]);

    // Route polylines: draw only in mapa-ruteo (same map instance); then fitBounds to route
    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return;
        routePolylinesRef.current.forEach((p) => p && p.setMap && p.setMap(null));
        routePolylinesRef.current = [];
        if (!routeLegs || routeLegs.length === 0) return;

        const bounds = new window.google.maps.LatLngBounds();

        routeLegs.forEach((leg) => {
            const path = leg.path;
            const color = leg.color || "#0000ff";
            if (!path || path.length < 2) return;
            path.forEach((p) => bounds.extend(new window.google.maps.LatLng(p.lat, p.lng)));
            const polyline = new window.google.maps.Polyline({
                path: path.map((p) => ({ lat: p.lat, lng: p.lng })),
                strokeColor: color,
                strokeOpacity: 1,
                strokeWeight: 5,
                map,
            });
            routePolylinesRef.current.push(polyline);
        });

        if (!bounds.isEmpty()) {
            const padding = 80;
            map.fitBounds(bounds, padding);
            // Run fitBounds again after a tick so it wins over any center/zoom effect and layout is settled
            const id = setTimeout(() => {
                if (!bounds.isEmpty()) map.fitBounds(bounds, padding);
            }, 100);
            return () => clearTimeout(id);
        }
    }, [map, routeLegs]);

    return (
        <div
            ref={mapRef}
            id="mapa-ruteo"
            className="w-full h-full"
            style={{ minHeight: "400px" }}
        />
    );
}
